import React, { useEffect, useState, useContext, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import ItemGenerated from "components/shared/ItemGenerated";
import FormFooter from "shared/components/FormElements/FormFooter";
import NotFound from "shared/components/UIElements/NotFound";
import AreYouSureModal from "components/shared/AreYouSureModal";
import GenericCard from "components/ui/cards/GenericCard";
import CreateOppositeServiceButton from "components/shared/CreateOppositeServiceButton";

import Input from "shared/components/FormElements/Input";
import CustomSelect from "shared/components/FormElements/Select";
import ServiceDaysPicker from "shared/components/FormElements/ServiceDaysPicker";
import MapWithTransferTimesForBundled from "components/shared/MapWithTransferTimesForBundled";

import { VALIDATOR_REQUIRE, VALIDATOR_NONE } from "shared/util/validators";
import { checkIsPickUp } from "shared/utility/types";
import {
  isActiveOptions_tr,
  isActiveOptions_en,
  personelServiceRouteTypes_tr,
  personelServiceRouteTypes_en,
  yesNoOptions_tr,
  yesNoOptions_en,
} from "shared/util/types";

import { AuthContext } from "shared/context/auth-context";
import { StoreContext } from "shared/context/store-context";
import { getCurrentLanguage } from "shared/utility/lang";
import { isPhoneAppAdminUiEnabled } from "config/ConfigUtils";

import { useBundledRouteApi } from "./hooks/useBundledRouteApi";
import RideSkipRosterPanel, { RIDE_SKIP_SERVICE_KINDS } from "features/ride-skip/RideSkipRosterPanel";
import { isRideSkipUiEnabled } from "features/ride-skip/constants";

// Independent Bundled (multi-school) student route authoring.
//
// The operator picks students across ANY schools; the served schools are derived automatically.
// The route is authored as an ORDERED stops[] chain (student homes + school institutions), shown
// on MapWithTransferTimesForBundled. "Optimize" runs the paired-shipment (Pickup-and-Delivery)
// solver so students near each other but bound for different schools are still sequenced optimally.
// Persists to the dedicated BundledRoute collection via /api/bundled-route (useBundledRouteApi).

const buildKey = (kind, refId) => `${kind}:${String(refId)}`;
const schoolIdOf = (student) => {
  const s = student?.school;
  if (!s) return null;
  return String(s.id || s._id || s);
};

// The anchor stop is the one whose time the operator sets directly: last school (PICK_UP) or
// first school (DROP_OFF). Every other stop time is derived from it in the map component.
const pickAnchorTimeFromStops = (stopsArr, pickup) => {
  const list = Array.isArray(stopsArr) ? stopsArr : [];
  const schools = list.filter((s) => s.kind === "SCHOOL");
  const anchor = pickup
    ? schools[schools.length - 1] || list[list.length - 1]
    : schools[0] || list[0];
  return anchor?.time || "";
};

const BundledRouteItem = ({ itemId, onClose, isNewItem, gotoItem, DeleteItem, UpdateItem, InsertItem }) => {
  const currentLanguage = getCurrentLanguage();
  const { t } = useTranslation();
  const auth = useContext(AuthContext);
  const showPhoneAppAdminUi = isPhoneAppAdminUiEnabled(auth?.company?.features);
  const { students, studentOptions, driverOptions, schools, bundledRoutes } = useContext(StoreContext);

  const { createBundledRoute, updateBundledRoute, deleteBundledRoute, optimizeBundledRoute, isLoading } =
    useBundledRouteApi();

  const [isItemLoaded, setIsItemLoaded] = useState(false);
  const [loadedItem, setLoadedItem] = useState(null);
  const [generatedItemId, setGeneratedItemId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isOptimizing, setIsOptimizing] = useState(false);

  const getRouteTypeOptions = () =>
    currentLanguage === "tr" ? personelServiceRouteTypes_tr : personelServiceRouteTypes_en;
  const getIsActiveOptions = () => (currentLanguage === "tr" ? isActiveOptions_tr : isActiveOptions_en);
  const getYesNoOptions = () => (currentLanguage === "tr" ? yesNoOptions_tr : yesNoOptions_en);

  // --- Form state ---
  const [name, setName] = useState("");
  const [plate, setPlate] = useState("");
  const [routeType, setRouteType] = useState("PICK_UP");
  const [isActive, setIsActive] = useState(true);
  const [useUsersDetails, setUseUsersDetails] = useState(true);
  const [serviceDays, setServiceDays] = useState([]);
  const [driverIds, setDriverIds] = useState([]);
  const [currentStudents, setCurrentStudents] = useState([]); // student ids (roster)
  const [stops, setStops] = useState([]); // ordered [{ key, kind, refId, name, location, time }]
  const [anchorTime, setAnchorTime] = useState(""); // single reference time (school arrival/departure)
  const [metrics, setMetrics] = useState({ distanceKm: undefined, durationMin: undefined });

  const isPickUp = checkIsPickUp(routeType);

  // --- Reconcile the ordered stops from the roster (preserving order + times) ---
  const reconcileStops = useCallback(
    (prevStops, studentIds, direction) => {
      const pickup = checkIsPickUp(direction);
      const timeByKey = new Map((prevStops || []).map((s) => [buildKey(s.kind, s.refId), s.time]));

      const rosterStudents = studentIds
        .map((id) => students.find((st) => String(st.id) === String(id)))
        .filter(Boolean);
      const distinctSchoolIds = [...new Set(rosterStudents.map((s) => schoolIdOf(s)).filter(Boolean))];

      const desired = new Set([
        ...studentIds.map((id) => buildKey("STUDENT", id)),
        ...distinctSchoolIds.map((id) => buildKey("SCHOOL", id)),
      ]);

      const makeStudent = (id) => {
        const st = students.find((s) => String(s.id) === String(id));
        if (!st) return null;
        const location = pickup ? st.locationgeton : st.locationgetoff;
        return { key: buildKey("STUDENT", id), kind: "STUDENT", refId: String(id), name: st.name, location, time: timeByKey.get(buildKey("STUDENT", id)) || "" };
      };
      const makeSchool = (id) => {
        const sc = schools.find((s) => String(s.id) === String(id) || String(s._id) === String(id));
        if (!sc) return null;
        return { key: buildKey("SCHOOL", id), kind: "SCHOOL", refId: String(id), name: sc.name, location: sc.location, time: timeByKey.get(buildKey("SCHOOL", id)) || "" };
      };

      const kept = (prevStops || [])
        .filter((s) => desired.has(buildKey(s.kind, s.refId)))
        .map((s) => (s.kind === "STUDENT" ? makeStudent(s.refId) : makeSchool(s.refId)))
        .filter(Boolean);
      const keptKeys = new Set(kept.map((s) => s.key));

      const missingStudents = studentIds
        .filter((id) => !keptKeys.has(buildKey("STUDENT", id)))
        .map(makeStudent)
        .filter(Boolean);
      const missingSchools = distinctSchoolIds
        .filter((id) => !keptKeys.has(buildKey("SCHOOL", id)))
        .map(makeSchool)
        .filter(Boolean);

      const tail = pickup ? [...missingStudents, ...missingSchools] : [...missingSchools, ...missingStudents];
      return [...kept, ...tail];
    },
    [students, schools],
  );

  // --- Load existing item ---
  // Read from the global store (like VehicleRouteItem), not a fresh DB fetch. The store list items
  // are populated identically to GET /bundled-route/:id (same ROUTE_POPULATE), and save/delete keep
  // the store in sync, so this avoids a redundant round-trip on every open.
  const fetchItem = useCallback(() => {
    setIsItemLoaded(false);
    if (isNewItem || !itemId) {
      setIsItemLoaded(true);
      return;
    }
    const route = (bundledRoutes || []).find((r) => String(r.id) === String(itemId));
    if (route) {
      setLoadedItem(route);
      setName(route.name || "");
      setPlate(route.plate || "");
      setRouteType(checkIsPickUp(route.routeType) ? "PICK_UP" : "DROP_OFF");
      setIsActive(route.isActive !== false);
      setUseUsersDetails(route.useUsersDetails !== false);
      setServiceDays(Array.isArray(route.serviceDays) ? route.serviceDays : []);
      setDriverIds((route.driver || []).map((d) => String(d.id || d)));
      setCurrentStudents((route.student || []).map((s) => String(s.id || s)));
      setMetrics({ distanceKm: route.totalDistance, durationMin: route.totalDuration });

      const rebuilt = (route.stops || [])
        .map((s) => {
          const refId = s.kind === "STUDENT" ? String(s.student?.id || s.student) : String(s.school?.id || s.school);
          const name =
            s.kind === "STUDENT" ? s.student?.name || "" : s.school?.name || "";
          return {
            key: buildKey(s.kind, refId),
            kind: s.kind,
            refId,
            name,
            location: s.location,
            time: s.time || "",
          };
        })
        .filter((s) => s.refId && s.refId !== "undefined");
      setStops(rebuilt);
      setAnchorTime(pickAnchorTimeFromStops(rebuilt, checkIsPickUp(route.routeType)));
    }
    setIsItemLoaded(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemId, isNewItem, bundledRoutes]);

  useEffect(() => {
    fetchItem();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemId]);

  // Rebuild stops whenever the roster or direction changes (preserves order + entered times).
  useEffect(() => {
    if (!isItemLoaded) return;
    setStops((prev) => reconcileStops(prev, currentStudents, routeType));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStudents, routeType, isItemLoaded]);

  const handleReorder = useCallback((newStops) => setStops(newStops), []);
  const handleAnchorTimeChange = useCallback((value) => setAnchorTime(value), []);
  // Apply the map-derived stop times, but only when something actually changed — this equality
  // guard prevents a render loop (compute → setStops → re-render → compute → ...).
  const handleComputedTimes = useCallback((entries) => {
    setStops((prev) => {
      const byKey = new Map(entries.map((e) => [e.key, e.time]));
      let changed = false;
      const next = prev.map((s) => {
        if (byKey.has(s.key) && byKey.get(s.key) !== s.time) {
          changed = true;
          return { ...s, time: byKey.get(s.key) };
        }
        return s;
      });
      return changed ? next : prev;
    });
  }, []);
  const handleMetrics = useCallback((m) => setMetrics(m), []);

  const handleOptimize = useCallback(async () => {
    if (currentStudents.length < 2) return;
    setIsOptimizing(true);
    const result = await optimizeBundledRoute(currentStudents, checkIsPickUp(routeType));
    setIsOptimizing(false);
    if (!result || !Array.isArray(result.stops)) return;

    setStops((prev) => {
      const byKey = new Map(prev.map((s) => [s.key, s]));
      const ordered = [];
      const used = new Set();
      for (const rs of result.stops) {
        const key = buildKey(rs.kind, rs.id);
        const s = byKey.get(key);
        if (s && !used.has(key)) {
          ordered.push(s);
          used.add(key);
        }
      }
      for (const s of prev) if (!used.has(s.key)) ordered.push(s);
      return ordered;
    });
    if (typeof result.totalDistance === "number" && typeof result.totalDuration === "number") {
      setMetrics({ distanceKm: result.totalDistance, durationMin: result.totalDuration });
    }
  }, [currentStudents, routeType, optimizeBundledRoute]);

  const mapStops = useMemo(() => stops, [stops]);

  const studentsWithoutLocation = useMemo(
    () =>
      currentStudents
        .map((id) => students.find((s) => String(s.id) === String(id)))
        .filter(Boolean)
        .filter((st) => {
          const loc = isPickUp ? st.locationgeton : st.locationgetoff;
          return !loc || typeof loc.lat !== "number" || typeof loc.lng !== "number";
        }),
    [currentStudents, students, isPickUp],
  );

  const itemSubmitHandler = useCallback(async () => {
    const payload = {
      name,
      routeType,
      driver: driverIds,
      plate,
      isActive,
      useUsersDetails,
      serviceDays: Array.isArray(serviceDays) ? serviceDays : [],
      student: currentStudents,
      stops: stops.map((s) => ({
        kind: s.kind,
        student: s.kind === "STUDENT" ? s.refId : undefined,
        school: s.kind === "SCHOOL" ? s.refId : undefined,
        time: s.time || "",
      })),
      ...(metrics.distanceKm !== undefined ? { totalDistance: metrics.distanceKm } : {}),
      ...(metrics.durationMin !== undefined ? { totalDuration: metrics.durationMin } : {}),
    };

    const saved = isNewItem ? await createBundledRoute(payload) : await updateBundledRoute(itemId, payload);
    if (!saved) return;

    if (isNewItem) {
      InsertItem(saved);
      setGeneratedItemId(saved.id);
    } else {
      UpdateItem(saved);
      setLoadedItem(saved);
      window.toast?.success(t("updateSuccess"));
    }
  }, [
    name,
    routeType,
    driverIds,
    plate,
    isActive,
    useUsersDetails,
    serviceDays,
    currentStudents,
    stops,
    metrics,
    isNewItem,
    itemId,
    createBundledRoute,
    updateBundledRoute,
    InsertItem,
    UpdateItem,
    t,
  ]);

  const handleDelete = useCallback(async () => {
    const ok = await deleteBundledRoute(itemId);
    setShowDeleteModal(false);
    if (ok) {
      DeleteItem(itemId);
      onClose();
    }
  }, [deleteBundledRoute, itemId, DeleteItem, onClose]);

  if (isItemLoaded && !isNewItem && !loadedItem) {
    return <NotFound item="vehicleRoute" />;
  }

  if (generatedItemId) {
    return (
      <ItemGenerated
        itemType="vehicleRoute"
        onGotoNewItem={() => gotoItem(generatedItemId)}
        onCreateNewItem={() => setGeneratedItemId(null)}
        onClose={() => {
          setGeneratedItemId(null);
          onClose();
        }}
      />
    );
  }

  const formValid = name.trim().length > 0 && currentStudents.length > 0;

  return (
    <React.Fragment>
      <AreYouSureModal
        showModal={showDeleteModal}
        text={t("areYouSure")}
        onClose={() => setShowDeleteModal(false)}
        onConfirmed={handleDelete}
      />

      {isItemLoaded && (
        <div className="vertical-flex">
          <GenericCard title={t("routeInfo")} dismissible={false} style={{ paddingBottom: "0px", marginBottom: "20px" }}>
            <div className="horizontal-flex">
              <div style={{ flex: 2 }}>
                <CustomSelect
                  options={getRouteTypeOptions()}
                  id="routeType"
                  onInput={(_id, value) => setRouteType(value)}
                  label={t("routeType")}
                  description={t("routeTypeDescription")}
                  initialValue={
                    getRouteTypeOptions().find((o) => o.value === (loadedItem?.routeType || "PICK_UP")) ||
                    getRouteTypeOptions()[0]
                  }
                  initialValid={true}
                />
              </div>
              <div style={{ flex: 3 }}>
                <Input
                  id="name"
                  element="input"
                  type="text"
                  label={t("routeName") + " *"}
                  validators={[VALIDATOR_REQUIRE()]}
                  errorText={t("requireField", { item: t("name") })}
                  initialValue={loadedItem?.name}
                  initialValid={!isNewItem}
                  onInput={(_id, value) => setName(value)}
                />
              </div>
              <div style={{ flex: 2 }}>
                <CustomSelect
                  options={getIsActiveOptions()}
                  id="isActive"
                  label={t("routeStatus")}
                  description={t("routeStatusDescription")}
                  onInput={(_id, value) => setIsActive(value)}
                  initialValue={loadedItem?.isActive === false ? getIsActiveOptions()[0] : getIsActiveOptions()[1]}
                  initialValid={true}
                />
              </div>
              {showPhoneAppAdminUi && (
                <div style={{ flex: 2 }}>
                  <CustomSelect
                    options={getYesNoOptions()}
                    id="useUsersDetails"
                    onInput={(_id, value) => setUseUsersDetails(value)}
                    initialValue={
                      getYesNoOptions().find((o) => o.value === loadedItem?.useUsersDetails) || getYesNoOptions()[1]
                    }
                    initialValid={true}
                    label={t("showPassengersTime")}
                    description={t("showPassengersTimeDescription")}
                  />
                </div>
              )}
              <div
                style={{
                  flex: 1,
                  display: "flex",
                  alignItems: "flex-end",
                  alignSelf: "stretch",
                  paddingBottom: "1.3rem",
                }}
              >
                <CreateOppositeServiceButton
                  loadedItem={loadedItem}
                  isNewItem={isNewItem}
                  InsertItem={InsertItem}
                  gotoItem={gotoItem}
                  serviceVariant="bundledroute"
                />
              </div>
            </div>
          </GenericCard>

          <GenericCard title={t("serviceDays.cardTitle")} style={{ paddingBottom: "0px", marginBottom: "20px" }}>
            <ServiceDaysPicker
              id="serviceDays"
              onInput={(_id, value) => setServiceDays(value)}
              initialValue={loadedItem?.serviceDays || []}
              label={t("serviceDays.label")}
            />
          </GenericCard>

          {!isNewItem && isRideSkipUiEnabled(auth?.company) && (
            <GenericCard title={t("rideSkip.panelTitle")} style={{ paddingBottom: "0px", marginBottom: "20px" }}>
              <RideSkipRosterPanel
                serviceKind={RIDE_SKIP_SERVICE_KINDS.BUNDLED_ROUTE}
                serviceId={itemId}
                students={students.filter((s) => currentStudents.includes(String(s.id || s._id)))}
                timezone={auth?.company?.timezone}
              />
            </GenericCard>
          )}

          <GenericCard title={t("serviceInfo")} style={{ paddingBottom: "0px", marginBottom: "20px" }}>
            <div className="horizontal-flex" style={{ marginBottom: "0px" }}>
              <div style={{ flex: 3 }}>
                <CustomSelect
                  options={driverOptions}
                  id="driver"
                  isMulti={true}
                  onInput={() => {}}
                  label={t("driversForRoute")}
                  initialValue={driverOptions.filter((o) => driverIds.includes(String(o.value)))}
                  initialValid={true}
                  fireChange={(val) => setDriverIds((val || []).map(String))}
                />
              </div>
              <div style={{ flex: 2 }}>
                <Input
                  id="plate"
                  element="input"
                  type="text"
                  label={t("plateForRoute")}
                  validators={[VALIDATOR_NONE()]}
                  onInput={(_id, value) => setPlate(value)}
                  initialValue={loadedItem?.plate}
                  initialValid={true}
                />
              </div>
            </div>

            <div>
              <CustomSelect
                options={studentOptions}
                id="student"
                isMulti={true}
                onInput={() => {}}
                label={t("passengersIntheRoute")}
                initialValue={studentOptions.filter((o) => currentStudents.includes(String(o.value)))}
                initialValid={true}
                fireChange={(val) => setCurrentStudents((val || []).map(String))}
              />
              {studentsWithoutLocation.length > 0 && (
                <div
                  style={{
                    backgroundColor: "#fff3cd",
                    border: "1px solid #ffeaa7",
                    borderRadius: "4px",
                    padding: "12px",
                    marginTop: "12px",
                    color: "#856404",
                  }}
                >
                  <strong>{t("warnings")}</strong> <br />
                  {t("theseStudentsHaveNoLocation")}: {studentsWithoutLocation.map((s) => s?.name || "?").join(", ")}
                </div>
              )}
            </div>
          </GenericCard>

          {stops.length > 0 && (
            <React.Fragment>
              <div className="divider" />
              <div style={{ display: "flex", flexDirection: "row", columnGap: "10px" }}>
                <MapWithTransferTimesForBundled
                  stops={mapStops}
                  isPickup={isPickUp}
                  anchorTime={anchorTime}
                  onAnchorTimeChange={handleAnchorTimeChange}
                  onReorder={handleReorder}
                  onComputedTimes={handleComputedTimes}
                  onOptimize={handleOptimize}
                  isOptimizing={isOptimizing}
                  savedTotalDistanceKm={loadedItem?.totalDistance}
                  savedTotalDurationMin={loadedItem?.totalDuration}
                  onMetrics={handleMetrics}
                />
              </div>
            </React.Fragment>
          )}

          <FormFooter
            isNewItem={isNewItem}
            onUpdate={itemSubmitHandler}
            onDelete={() => setShowDeleteModal(true)}
            isUpdating={isLoading}
            disabled={!formValid || isLoading || isOptimizing}
          />
        </div>
      )}
    </React.Fragment>
  );
};

export default BundledRouteItem;
